import React from 'react';
import { useIntl } from 'react-intl';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { CenteredFlexBox, ColumnFlexBox } from '../../FlexBox';
import useTranslate from '../../../Hooks/useTranslate';
import { SCOPES, scopesOn } from '../binding/scopes';
import { PREDICATE_OPERATORS } from '../binding/predicate';

/** How the terms are read together. */
const JOINS = [
  { value: 'all', label: 'JOIN_ALL' },
  { value: 'any', label: 'JOIN_ANY' },
];

/** The operators comparing what was read against nothing, so no value is asked for. */
const NEEDS_NO_VALUE = ['set', 'unset'];

/** A term as it is held before anything has been chosen for it. */
const emptyTerm = (scope) => ({ scope, key: '', op: 'eq', value: '' });

/**
 * The predicate as the join it uses and the terms under it.
 *
 * A single term saved on its own reads as one term joined by `all`, so the editor has one shape to
 * draw whatever a layout carries.
 */
const held = (predicate) => {
  if (!predicate) return { join: 'all', terms: [] };

  if (Array.isArray(predicate.all))
    return { join: 'all', terms: predicate.all };

  if (Array.isArray(predicate.any))
    return { join: 'any', terms: predicate.any };

  return { join: 'all', terms: [predicate] };
};

/** The predicate a join and its terms make, or nothing where no term says anything. */
const composed = (join, terms) => {
  const named = terms.filter(({ key }) => key);

  if (named.length === 0) return undefined;

  return { [join]: named };
};

/**
 * Builds the predicate deciding whether an entry is shown.
 *
 * Every part of a term is chosen rather than typed: the scope it reads, the key in it, and how that
 * is compared. The key is offered from the catalogue where the scope reads the record, and from the
 * entries beside this one where it reads the form.
 *
 * @param {Object} props - Component props.
 * @param {String} props.label - What the setting is called.
 * @param {Object} props.value - The predicate held.
 * @param {String} props.surface - The surface the entry belongs to, which decides the scopes offered.
 * @param {Array} props.catalogue - The keys the section can show.
 * @param {Array} props.siblings - The entries beside this one, as `{ key, label }`.
 * @param {Array} props.parameters - The runtime values the page publishes, by name.
 * @param {Function} props.onChange - Receives the new predicate, undefined where it says nothing.
 *
 * @returns {React.JSX.Element}
 */
const PredicateField = function PredicateField({
  label,
  value = null,
  surface = 'list',
  catalogue = [],
  siblings = [],
  parameters = [],
  onChange = () => {},
}) {
  const intl = useIntl();
  const translate = useTranslate();
  const offered = SCOPES.filter(({ value: name }) =>
    scopesOn(surface).includes(name)
  );
  const firstScope = offered[0]?.value ?? 'context';
  // A term is built a control at a time and says nothing until it names a key, so the ones being
  // built are held here while only the finished ones are published.
  const [{ join, terms }, setHeld] = React.useState(() => held(value));

  const write = (nextJoin, nextTerms) => {
    setHeld({ join: nextJoin, terms: nextTerms });
    onChange(composed(nextJoin, nextTerms));
  };

  const updateTerm = (index, changes) =>
    write(
      join,
      terms.map((term, at) => (at === index ? { ...term, ...changes } : term))
    );

  /** What a term may name, by the scope it reads. */
  const keysFor = (scope) => {
    if (scope === 'context')
      return parameters.map((name) => ({ key: name, label: name }));

    if (scope === 'field')
      return siblings.map(({ key, label: wording }) => ({
        key,
        label: wording || key,
      }));

    return catalogue.map(({ key, label: wording }) => ({
      key,
      label: wording || key,
    }));
  };

  return (
    <ColumnFlexBox gap="8px" sx={{ width: '100%' }}>
      <CenteredFlexBox gap="12px">
        <Typography variant="caption" sx={{ opacity: '70%', flex: 1 }}>
          {label}
        </Typography>
        {terms.length > 1 && (
          <Select
            size="small"
            value={join}
            onChange={(e) => write(e.target.value, terms)}
            sx={{ width: '160px' }}
          >
            {JOINS.map(({ value: name, label: wording }) => (
              <MenuItem key={name} value={name}>
                {translate(wording)}
              </MenuItem>
            ))}
          </Select>
        )}
      </CenteredFlexBox>
      {terms.map((term, index) => {
        const options = keysFor(term.scope ?? firstScope);
        const chosen =
          options.find((one) => one.key === term.key) ??
          (term.key ? { key: term.key, label: term.key } : null);

        return (
          <CenteredFlexBox key={`term-${String(index)}`} gap="8px">
            <Select
              size="small"
              value={term.scope ?? firstScope}
              onChange={(e) =>
                updateTerm(index, { scope: e.target.value, key: '' })
              }
              sx={{ width: '120px', flex: 'none' }}
            >
              {offered.map(({ value: name, label: wording }) => (
                <MenuItem key={name} value={name}>
                  {translate(wording)}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              displayEmpty
              value={chosen ? chosen.key : ''}
              onChange={(e) => updateTerm(index, { key: e.target.value })}
              sx={{ flex: 1, minWidth: '120px' }}
            >
              {!chosen && (
                <MenuItem value="">{intl.messages.CHOOSE_A_KEY}</MenuItem>
              )}
              {options.map(({ key, label: wording }) => (
                <MenuItem key={key} value={key}>
                  {translate(wording)}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              value={term.op ?? 'eq'}
              onChange={(e) => updateTerm(index, { op: e.target.value })}
              sx={{ width: '150px', flex: 'none' }}
            >
              {PREDICATE_OPERATORS.map(({ value: name, label: wording }) => (
                <MenuItem key={name} value={name}>
                  {translate(wording)}
                </MenuItem>
              ))}
            </Select>
            {!NEEDS_NO_VALUE.includes(term.op) && (
              <TextField
                size="small"
                label={intl.messages.VALUE}
                value={term.value ?? ''}
                onChange={(e) => updateTerm(index, { value: e.target.value })}
                sx={{ width: '150px', flex: 'none' }}
              />
            )}
            <IconButton
              size="small"
              onClick={() =>
                write(
                  join,
                  terms.filter((_, at) => at !== index)
                )
              }
            >
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </CenteredFlexBox>
        );
      })}
      <Box>
        <Button
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={() => write(join, [...terms, emptyTerm(firstScope)])}
        >
          {intl.messages.ADD_CONDITION}
        </Button>
      </Box>
    </ColumnFlexBox>
  );
};

export default PredicateField;
